/**
 * BLU-1436: the Stock type picker for one pick-and-pack product code, on the Products
 * PAP tab. One pick saves the code (every live PAP SKU starting with it), so the scope
 * line says so before anyone picks. The words come from audienceOptions, the same
 * helper Base garments uses.
 */
import { useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectLabel, SelectGroup, SelectTrigger, SelectValue } from '@/components/ui/select';
import { audienceLabel, audienceOptions, parseAudienceValue } from '../lib/base-garments-audience';
import { STOCK_TYPE_MIXED } from '../lib/pap-stock-type';

export interface CodeAudienceSelectProps {
  code: string;
  /** `own`, `all`, `client:<id>`, or STOCK_TYPE_MIXED. */
  value: string;
  /** The connected clients, for "Labelled for {client}". */
  clients: ReadonlyArray<{ id: string; name: string }>;
  onChange: (value: string) => void;
  saving?: boolean;
  disabled?: boolean;
}

/** A pick inside a clickable row must never open the row. */
const stop = (event: MouseEvent) => event.stopPropagation();

export function CodeAudienceSelect({ code, value, clients, onChange, saving = false, disabled = false }: CodeAudienceSelectProps) {
  const { t } = useTranslation('inventory');
  const mixed = value === STOCK_TYPE_MIXED;

  const options = useMemo(() => {
    const list = audienceOptions(t, clients);
    // The code's own client is always listed, even when its link has lapsed.
    if (!mixed && !list.some((option) => option.value === value)) {
      const choice = parseAudienceValue(value);
      const name = clients.find((client) => client.id === choice.clientOrganizationId)?.name ?? null;
      list.push({ value, label: audienceLabel(t, choice.audience, name) });
    }
    return list;
  }, [t, clients, value, mixed]);

  return (
    <div className="inline-flex items-center gap-1.5" onClick={stop}>
      <Select
        value={mixed ? '' : value}
        onValueChange={(next) => {
          if (next !== value) onChange(next);
        }}
        disabled={disabled || saving}
      >
        <SelectTrigger
          className="h-8 w-auto min-w-[10rem] max-w-[14rem] text-xs"
          aria-label={t('products.stockType.aria', { code })}
          title={mixed ? t('products.stockType.mixedHint', { code }) : undefined}
        >
          <SelectValue placeholder={t('products.stockType.mixed')} />
        </SelectTrigger>
        <SelectContent onClick={stop}>
          <SelectGroup>
            <SelectLabel className="max-w-[16rem] whitespace-normal text-xs font-normal text-muted-foreground">
              {t('products.stockType.scope', { code })}
            </SelectLabel>
            {mixed && (
              <SelectLabel className="max-w-[16rem] whitespace-normal text-xs font-normal text-muted-foreground">
                {t('products.stockType.mixedHint', { code })}
              </SelectLabel>
            )}
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {saving && (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">{t('products.stockType.saving')}</span>
        </>
      )}
    </div>
  );
}
